"use client";
import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Footer from '../../components/Footer';
import Skeleton from '../../components/ui/Skeleton';
import {
  Badge,
  GradientText,
  ScrollReveal,
  DividerRow,
  StaggerGroup,
  StaggerItem,
  Pill,
} from '../../../components/shared';
import type { DividerRowItem } from '../../../components/shared';
import {
  MegaphoneIcon,
  SearchIcon,
  WalletIcon,
  ClockIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  ArrowRightIcon,
  UsersIcon,
  DollarIcon,
  ShieldCheckIcon,
  GlobeIcon,
  SparklesIcon,
  TrendingUpIcon,
} from '../../../components/shared/icons';

type ExploreFundraiser = {
  id?: string;
  title: string;
  description?: string;
  target?: number;
  raised?: number;
  walletAddress?: string;
  owner?: string;
  createdAt?: string | Date;
  expiresAt?: string | Date;
  active?: boolean;
  recentDonors?: string[];
};

type SortKey = 'newest' | 'ending' | 'raised' | 'donors';

const SORT_TABS: { key: SortKey; label: string; icon: typeof SparklesIcon }[] = [
  { key: 'newest', label: 'Newly Added', icon: SparklesIcon },
  { key: 'ending', label: 'Ending Soon', icon: ClockIcon },
  { key: 'raised', label: 'Most Raised', icon: TrendingUpIcon },
  { key: 'donors', label: 'Most Donors', icon: UsersIcon },
];

/**
 * Fundraiser Explore page - the genuinely-missing page from the client
 * mockup's FundraiserExplorePage (the other 8 transcript pages already had
 * real-data equivalents built; this one never did). Per the standing
 * "restyle, keep real data" rule, this is NOT the mockup's hardcoded
 * `campaigns` array - it's a real public browse view backed by a new GET
 * handler on /api/fundraisers that lists every active+approved+public
 * fundraiser (lib/models/Fundraiser.ts already had a compound index for
 * exactly this query, so the listing endpoint was clearly intended).
 *
 * Deliberate divergences from the mockup, matching the pattern already used
 * on Charities/Fundraisers/Donate:
 * - Stats row: 2 real numbers (live count, live sum of `raised`) plus 1
 *   qualitative trust statement, replacing the mockup's fabricated
 *   "342 Active / $2.45M Raised / 25,138 Lives Impacted".
 * - No category filter grid: the real Fundraiser schema has no `category`
 *   field, so the mockup's Medical/Emergency/Education/... facet is omitted
 *   rather than invented.
 * - Sort tabs map to four real fields (createdAt, expiresAt, raised,
 *   recentDonors.length) instead of an undefined "Trending" algorithm.
 * - No single "Featured" hero campaign - there's no `featured` flag in the
 *   schema, and singling one out would just duplicate the first grid card.
 */
export default function FundraiserExplorePage() {
  const router = useRouter();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('newest');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(12);
  const [list, setList] = useState<ExploreFundraiser[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totalRaised, setTotalRaised] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      params.set('sort', sort);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      const res = await fetch(`/api/fundraisers?${params.toString()}`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      setList(Array.isArray(data.fundraisers) ? data.fundraisers : []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      setTotalRaised(typeof data.totalRaised === 'number' ? data.totalRaised : 0);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
      setList([]);
    }
  }, [q, sort, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  function changeSort(next: SortKey) {
    setSort(next);
    setPage(1);
  }

  const statItems: DividerRowItem[] = [
    {
      key: 'count',
      content: (
        <>
          <MegaphoneIcon size={18} className="shrink-0" style={{ color: '#fca5a5' }} />
          <span className="min-w-0">
            <span className="block text-base sm:text-lg font-extrabold leading-tight" style={{ color: '#f1f5f9' }}>
              {list !== null ? total : '—'}
            </span>
            <span className="block text-[11px] sm:text-xs font-medium leading-tight" style={{ color: 'var(--muted-text, #94a3b8)' }}>
              Active Fundraisers
            </span>
          </span>
        </>
      ),
    },
    {
      key: 'raised',
      content: (
        <>
          <DollarIcon size={18} className="shrink-0" style={{ color: '#fb923c' }} />
          <span className="min-w-0">
            <span className="block text-base sm:text-lg font-extrabold leading-tight" style={{ color: '#f1f5f9' }}>
              {list !== null ? `$${totalRaised.toLocaleString()}` : '—'}
            </span>
            <span className="block text-[11px] sm:text-xs font-medium leading-tight" style={{ color: 'var(--muted-text, #94a3b8)' }}>
              Total Raised
            </span>
          </span>
        </>
      ),
    },
    {
      key: 'trust',
      content: (
        <>
          <ShieldCheckIcon size={18} className="shrink-0" style={{ color: '#94a3b8' }} />
          <span className="min-w-0">
            <span className="block text-base sm:text-lg font-extrabold leading-tight" style={{ color: '#f1f5f9' }}>
              On-Chain
            </span>
            <span className="block text-[11px] sm:text-xs font-medium leading-tight" style={{ color: 'var(--muted-text, #94a3b8)' }}>
              Wallet-to-Wallet, No Middleman
            </span>
          </span>
        </>
      ),
    },
  ];

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{
        backgroundColor: '#0A0F1C',
        backgroundImage:
          'radial-gradient(circle at top, rgba(248,113,113,0.07), transparent 55%), radial-gradient(circle at bottom, rgba(0,212,255,0.06), transparent 55%)',
      }}
    >
      <main id="content" className="flex-1 w-full flex flex-col items-center">
        {/* Hero */}
        <section aria-labelledby="explore-heading" className="relative w-full px-4 pt-24 pb-4 sm:pt-28">
          <div className="mx-auto max-w-6xl">
            {/* Breadcrumb — lives above the glass card, never inside it */}
            <button
              onClick={() => router.push('/fundraisers')}
              className="mb-4 inline-flex items-center gap-1.5 text-xs font-medium transition-colors"
              style={{ color: 'var(--muted-text, #94a3b8)' }}
            >
              ← Back to Fundraisers
            </button>

            <ScrollReveal>
              <div
                className="relative overflow-hidden rounded-[28px] border px-5 py-8 sm:px-10 sm:py-10"
                style={{
                  borderColor: 'rgba(248,113,113,0.28)',
                  background: 'linear-gradient(180deg, rgba(16,24,39,0.75), rgba(10,15,28,0.9))',
                  boxShadow:
                    '0 0 0 1px rgba(248,113,113,0.08) inset, 0 30px 70px -30px rgba(0,0,0,0.8), 0 0 60px -20px rgba(248,113,113,0.22)',
                }}
              >
                <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
                  <div
                    className="absolute -top-24 left-1/3 h-72 w-72 rounded-full opacity-40 blur-3xl"
                    style={{ background: 'radial-gradient(circle, rgba(248,113,113,0.22), transparent 70%)' }}
                  />
                  <div
                    className="absolute bottom-0 right-0 h-72 w-72 rounded-full opacity-30 blur-3xl"
                    style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.18), transparent 70%)' }}
                  />
                </div>

                <div className="relative z-10">
                  <Badge
                    icon={<MegaphoneIcon size={14} />}
                    borderColor="rgba(248,113,113,0.4)"
                    backgroundColor="rgba(248,113,113,0.1)"
                    textColor="#fca5a5"
                  >
                    Explore Campaigns
                  </Badge>

                  <div className="mt-5 flex flex-wrap items-end justify-between gap-4">
                    <div>
                      <h1 id="explore-heading" className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">
                        <GradientText variant="brand">Explore Fundraisers</GradientText>
                      </h1>
                      <p className="mt-3 max-w-xl text-base sm:text-lg leading-relaxed" style={{ color: 'var(--muted-text, #94a3b8)' }}>
                        Browse every active, on-chain fundraising campaign and support one directly with your wallet.
                      </p>
                    </div>

                    <button
                      onClick={() => router.push('/fundraisers/create')}
                      className="inline-flex shrink-0 items-center gap-2 rounded-full bg-gradient-to-r from-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-[0_0_24px_-8px_rgba(248,113,113,0.5)] transition hover:-translate-y-0.5"
                    >
                      Create Fundraiser
                      <ArrowRightIcon size={16} />
                    </button>
                  </div>
                </div>

                <DividerRow
                  className="relative z-10 mt-8"
                  background="rgba(8,12,22,0.5)"
                  cellClassName="flex flex-1 items-center min-w-0 gap-2.5 px-4 py-3 sm:px-4 sm:py-3.5"
                  items={statItems}
                />
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Search + sort */}
        <section className="relative w-full px-4 pb-2">
          <div className="mx-auto max-w-6xl">
            <div
              className="rounded-3xl border p-4 sm:p-5"
              style={{ borderColor: 'rgba(248,113,113,0.2)', background: 'rgba(16,24,39,0.45)', backdropFilter: 'blur(18px)' }}
            >
              <div
                className="flex items-center gap-2.5 rounded-2xl border px-4 py-2.5"
                style={{ borderColor: 'rgba(248,113,113,0.25)', background: 'rgba(8,12,22,0.55)' }}
              >
                <SearchIcon size={16} className="shrink-0" style={{ color: '#fca5a5' }} />
                <input
                  value={q}
                  onChange={(e) => { setQ(e.target.value); setPage(1); }}
                  placeholder="Search campaigns by title"
                  className="flex-1 bg-transparent text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none"
                />
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {SORT_TABS.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <Pill
                      key={tab.key}
                      label={tab.label}
                      icon={<Icon size={14} />}
                      active={sort === tab.key}
                      onClick={() => changeSort(tab.key)}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Results */}
        <section aria-label="Fundraiser campaigns" className="relative w-full px-4 pb-10 pt-6">
          <div className="mx-auto max-w-6xl">
            {err ? (
              <div
                className="flex items-start gap-3 rounded-2xl border px-4 py-3.5 text-sm"
                style={{ borderColor: 'rgba(248,113,113,0.35)', background: 'rgba(127,29,29,0.18)', color: '#fecaca' }}
              >
                <AlertTriangleIcon size={18} className="mt-0.5 shrink-0" />
                <span>Error loading campaigns: {err}</span>
              </div>
            ) : list === null ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" aria-label="Loading campaigns">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-2xl border"
                    style={{
                      background: 'var(--glass-bg, rgba(16,24,39,0.55))',
                      borderColor: 'var(--color-card-border, rgba(91,127,255,0.18))',
                      backdropFilter: 'blur(18px)',
                    }}
                  >
                    <Skeleton className="h-5 w-2/3 bg-slate-700/60 rounded mb-3" />
                    <Skeleton className="h-3 w-1/2 bg-slate-700/40 rounded mb-3" />
                    <Skeleton className="h-3 w-3/4 bg-slate-700/30 rounded" />
                  </div>
                ))}
              </div>
            ) : list.length === 0 ? (
              <div
                className="flex flex-col items-center gap-3 rounded-2xl border px-6 py-14 text-center"
                style={{
                  background: 'var(--glass-bg, rgba(16,24,39,0.55))',
                  borderColor: 'var(--color-card-border, rgba(91,127,255,0.18))',
                }}
              >
                <MegaphoneIcon size={22} style={{ color: '#fca5a5' }} />
                <p className="text-sm" style={{ color: 'var(--muted-text, #94a3b8)' }}>
                  {q ? 'No campaigns match your search.' : 'No active campaigns yet - be the first to launch one.'}
                </p>
              </div>
            ) : (
              <StaggerGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {list.map((f, i) => (
                  <StaggerItem key={f.id ?? i}>
                    <CampaignCard f={f} />
                  </StaggerItem>
                ))}
              </StaggerGroup>
            )}

            {list && total > pageSize ? (
              <div className="mt-6 flex items-center justify-between text-xs" style={{ color: 'var(--muted-text, #94a3b8)' }}>
                <div>Page {page} of {Math.ceil(total / pageSize)}</div>
                <div className="inline-flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] disabled:opacity-40"
                    style={{ borderColor: 'rgba(248,113,113,0.4)' }}
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(Math.ceil(total / pageSize), p + 1))}
                    disabled={page >= Math.ceil(total / pageSize)}
                    className="rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] disabled:opacity-40"
                    style={{ borderColor: 'rgba(248,113,113,0.4)' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        {/* Trust strip */}
        <section aria-label="Trust signals" className="relative w-full px-4 pb-16">
          <div className="mx-auto max-w-6xl">
            <DividerRow
              items={[
                {
                  key: 'onchain',
                  content: (
                    <>
                      <GlobeIcon size={18} className="shrink-0" style={{ color: '#94a3b8' }} />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold leading-tight" style={{ color: '#f1f5f9' }}>On-Chain & Transparent</span>
                        <span className="block text-[11px] leading-tight" style={{ color: 'var(--muted-text, #94a3b8)' }}>Every donation is publicly verifiable</span>
                      </span>
                    </>
                  ),
                },
                {
                  key: 'p2p',
                  content: (
                    <>
                      <WalletIcon size={18} className="shrink-0" style={{ color: '#94a3b8' }} />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold leading-tight" style={{ color: '#f1f5f9' }}>Wallet-to-Wallet</span>
                        <span className="block text-[11px] leading-tight" style={{ color: 'var(--muted-text, #94a3b8)' }}>No platform custody, no middleman</span>
                      </span>
                    </>
                  ),
                },
                {
                  key: 'verified',
                  content: (
                    <>
                      <CheckCircleIcon size={18} className="shrink-0" style={{ color: '#94a3b8' }} />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold leading-tight" style={{ color: '#f1f5f9' }}>Reviewed Campaigns</span>
                        <span className="block text-[11px] leading-tight" style={{ color: 'var(--muted-text, #94a3b8)' }}>Only approved fundraisers are listed</span>
                      </span>
                    </>
                  ),
                },
              ]}
            />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function CampaignCard({ f }: { f: ExploreFundraiser }) {
  const target = f.target ?? 0;
  const raised = f.raised ?? 0;
  const pct = target > 0 ? Math.min(100, Math.round((raised / target) * 100)) : 0;
  const expires = f.expiresAt ? new Date(f.expiresAt) : null;
  const formattedExpires = expires && !isNaN(expires.getTime()) ? expires.toLocaleDateString() : null;
  const donorCount = Array.isArray(f.recentDonors) ? f.recentDonors.length : 0;

  return (
    <div
      className="group relative p-5 rounded-2xl border transition-all duration-300 hover:-translate-y-1"
      style={{
        background: 'var(--glass-bg, rgba(16,24,39,0.55))',
        borderColor: 'rgba(248,113,113,0.22)',
        backdropFilter: 'blur(18px)',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <h2 className="font-semibold text-base" style={{ color: '#f1f5f9' }}>{f.title}</h2>
        {f.active ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80' }}>
            <CheckCircleIcon size={11} /> Active
          </span>
        ) : null}
      </div>

      {f.description ? (
        <p className="mt-2 text-sm leading-relaxed line-clamp-2" style={{ color: 'var(--muted-text, #94a3b8)' }}>
          {f.description}
        </p>
      ) : null}

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span style={{ color: '#fca5a5' }}>Raised {raised.toLocaleString()}</span>
          <span style={{ color: 'var(--muted-text, #94a3b8)' }}>Goal {target.toLocaleString()}</span>
        </div>
        <div className="h-2 w-full rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div
            className="h-full rounded-full bg-gradient-to-r from-red-500 to-orange-400"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px]" style={{ color: 'var(--muted-text, #94a3b8)' }}>
        {f.walletAddress ? (
          <span className="inline-flex items-center gap-1">
            <WalletIcon size={13} />
            <span className="font-mono">{f.walletAddress.slice(0, 6)}...{f.walletAddress.slice(-4)}</span>
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1">
          <UsersIcon size={13} />
          {donorCount} {donorCount === 1 ? 'donor' : 'donors'}
        </span>
        {formattedExpires ? (
          <span className="inline-flex items-center gap-1">
            <ClockIcon size={13} />
            Ends {formattedExpires}
          </span>
        ) : null}
      </div>
    </div>
  );
}
